import React, { ChangeEvent, FunctionComponent } from "react";

import { Caption, CheckBoxComponentValue, ColSpan, GridColumnCount, GridGapSize, GridLayout, GridLayoutItem, Layout, SpacingSize } from "@multiweb/reactComponents";
import { LOC_ACTIVITY_NAME, LOC_BASIC_CONDITION, LOC_DESCRIPTION, LOC_INHERIT_PARTICIPANTS_FROM_PREVIOUS_ACTIVITY, LOC_KILL_CHILDREN, LOC_ONLINE_EVALUATED_CONDITION, LOC_ORDER, LOC_SHOW_IN_WF_HISTORY, LOC_TYPE_FOR_JOIN, LOC_TYPE_FOR_SPLIT } from "../../../../locKeys";
import { IActivity, WorkflowEditorProps } from "../../../../types";
import { ActivityType, IOTransitionType } from "../../../../workflowModel";
import { Edit, Memo, WfCheckBox, WfSelect } from "../../components";

type ActivityPropertiesTabProps = {
    readonly activity: IActivity;
    readonly updateActivity: (newActivity: IActivity) => void;
} & WorkflowEditorProps;

export const ActivityPropertiesTab: FunctionComponent<ActivityPropertiesTabProps> = ({ activity, hostKey, updateActivity }) => {
    const changeActivityName = (e: ChangeEvent<HTMLInputElement>) => {
        updateActivity({ ...activity, name: e.target.value });
    };

    const changeActivityOrder = (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = Number(e.target.value);

        if (isNaN(newValue))
            return;

        updateActivity({ ...activity, order: newValue });
    };

    const changeActivityDescription = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateActivity({ ...activity, description: e.target.value });
    };

    const changeActivityKillChildren = (value: CheckBoxComponentValue) => {
        updateActivity({ ...activity, killChildren: value ?? false });
    };

    const changeActivityCondition = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateActivity({ ...activity, condition: e.target.value });
    };

    const changeActivityRunCondition = (e: ChangeEvent<HTMLTextAreaElement>) => {
        updateActivity({ ...activity, runCondition: e.target.value });
    };

    const changeActivityShowInWfHistory = (value: CheckBoxComponentValue) => {
        updateActivity({ ...activity, showInWfHistory: value ?? false });
    };

    const changeActivityInheritParticipants = (value: CheckBoxComponentValue) => {
        updateActivity({ ...activity, inheritParticipants: value ?? false });
    };

    const changeActivityInputTransType = (e: ChangeEvent<HTMLSelectElement>) => {
        updateActivity({ ...activity, inputTransType: e.target.value as IOTransitionType });
    };

    return (
        <Layout spacingSizeHorizontal={SpacingSize.m} spacingSizeTop={SpacingSize.m}>
            <GridLayout columns={GridColumnCount.eight} gap={GridGapSize.two}>
                <GridLayoutItem colSpan={ColSpan.seven}>
                    <Caption locKey={LOC_ACTIVITY_NAME} />
                    <Edit qa={LOC_ACTIVITY_NAME} maxLength={64} value={activity.name} onChange={changeActivityName} {...{ hostKey }} />
                </GridLayoutItem>
                <GridLayoutItem colSpan={ColSpan.one}>
                    <Caption locKey={LOC_ORDER} />
                    <Edit qa={LOC_ORDER} type={"number"} value={activity.order} onChange={changeActivityOrder} {...{ hostKey }} />
                </GridLayoutItem>
                <GridLayoutItem colSpan={ColSpan.four}>
                    <Caption locKey={LOC_TYPE_FOR_JOIN} />
                    <WfSelect qa={LOC_TYPE_FOR_JOIN} disabled={activity.type === ActivityType.start || activity.type === ActivityType.end} value={activity.inputTransType} onChange={changeActivityInputTransType} {...{ hostKey }} >
                        <option>{IOTransitionType.and}</option>
                        <option>{IOTransitionType.xor}</option>
                    </WfSelect>
                </GridLayoutItem>
                <GridLayoutItem colSpan={ColSpan.four}>
                    <Caption locKey={LOC_TYPE_FOR_SPLIT} />
                    <Edit qa={LOC_TYPE_FOR_SPLIT} readOnly maxLength={255} value={activity.outputTransType} {...{ hostKey }} />
                </GridLayoutItem>
                <GridLayoutItem colSpan={ColSpan.eight}>
                    <Caption locKey={LOC_BASIC_CONDITION} />
                    <Memo qa={LOC_BASIC_CONDITION} value={activity.condition} onChange={changeActivityCondition} {...{ hostKey }} />
                </GridLayoutItem>
                <GridLayoutItem colSpan={ColSpan.eight}>
                    <Caption locKey={LOC_ONLINE_EVALUATED_CONDITION} />
                    <Memo qa={LOC_ONLINE_EVALUATED_CONDITION} value={activity.runCondition} onChange={changeActivityRunCondition} {...{ hostKey }} />
                </GridLayoutItem>
                <GridLayoutItem colSpan={ColSpan.four}>
                    <WfCheckBox qa={LOC_INHERIT_PARTICIPANTS_FROM_PREVIOUS_ACTIVITY} disabled={activity.type === ActivityType.start || activity.type === ActivityType.end} textLocKey={LOC_INHERIT_PARTICIPANTS_FROM_PREVIOUS_ACTIVITY} showFalseAsCrossed value={activity.inheritParticipants} onChange={changeActivityInheritParticipants} {...{ hostKey }} />
                </GridLayoutItem>
                <GridLayoutItem colSpan={ColSpan.four}>
                    <WfCheckBox qa={LOC_KILL_CHILDREN} textLocKey={LOC_KILL_CHILDREN} showFalseAsCrossed value={activity.killChildren} onChange={changeActivityKillChildren} {...{ hostKey }} />
                </GridLayoutItem>
                <GridLayoutItem colSpan={ColSpan.eight}>
                    <WfCheckBox qa={LOC_SHOW_IN_WF_HISTORY} textLocKey={LOC_SHOW_IN_WF_HISTORY} showFalseAsCrossed value={activity.showInWfHistory} onChange={changeActivityShowInWfHistory} {...{ hostKey }} />
                </GridLayoutItem>
                <GridLayoutItem colSpan={ColSpan.eight}>
                    <Caption locKey={LOC_DESCRIPTION} />
                    <Memo qa={LOC_DESCRIPTION} value={activity.description} onChange={changeActivityDescription} {...{ hostKey }} />
                </GridLayoutItem>
            </GridLayout>
        </Layout>
    );
};
